import { useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import { canonicalFor } from "@/lib/site";
import { events } from "@/lib/analytics";
import { submitLead } from "@/lib/leads";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const SERVICE_TYPES = [
  "Roof Installation",
  "Roof Repair",
  "Roof Inspection",
  "Emergency Service",
  "Storm Damage",
  "Not Sure Yet",
];

const SERVICE_AREAS = [
  "Vancouver",
  "Burnaby",
  "North Vancouver",
  "West Vancouver",
  "Richmond",
  "Surrey",
  "Coquitlam",
  "Port Moody",
  "New Westminster",
  "Delta",
  "Langley",
  "Maple Ridge",
  "Pitt Meadows",
  "Abbotsford",
  "Mission",
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: "",
    details: "",
  });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = (e) => {
    e.preventDefault();
    events.estimateFormSubmit();
    setSubmitted(true);
  };

  return (
    <>
      <Seo
        title="Contact | Crestline Roofing Ltd."
        description="Contact Crestline Roofing Ltd. for a free roofing estimate in Vancouver and the Fraser Valley. Call, email, or fill out our quick quote form."
        canonical={canonicalFor("/contact")}
      />
      <PageLayout>
        {() => (
          <main data-testid="contact-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Free Estimate"
              title="Get a Free Roofing Estimate"
              subtitle="48-hour response. No pressure. No commission games. Just a clean quote — from the team that treats your home like ours."
              image="https://images.unsplash.com/photo-1583345237708-add35a664d77?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Steep residential rooftop at golden hour — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[{ label: "Home", href: "/" }, { label: "Contact" }]}
            />

            <section className="relative bg-[var(--crest-charcoal)] py-20 md:py-28">
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                  {/* Contact info */}
                  <aside className="lg:col-span-4">
                    <div className="reveal flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Get in touch
                      </span>
                    </div>
                    <h3
                      className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight mb-8"
                      style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)" }}
                    >
                      Three ways to reach us.
                    </h3>

                    <div className="space-y-5">
                      <a
                        href="tel:+17789176817"
                        data-testid="contact-phone"
                        className="reveal flex items-start gap-4 p-5 bg-[#161616] border border-white/10 hover:border-[var(--crest-amber)]/50 transition-colors group"
                      >
                        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
                          <Phone size={16} strokeWidth={2} />
                        </div>
                        <div>
                          <div className="font-display uppercase tracking-[0.25em] text-[10px] text-white/50 mb-1.5">
                            Call us
                          </div>
                          <div className="font-display font-bold text-lg tracking-tight text-white group-hover:text-[var(--crest-amber)] transition-colors">
                            (778) 917-6817
                          </div>
                          <div className="font-body text-xs text-white/40 mt-1">
                            Same-day for active leaks
                          </div>
                        </div>
                      </a>

                      <a
                        href="mailto:info@crestlineroofing.ca"
                        data-testid="contact-email"
                        className="reveal flex items-start gap-4 p-5 bg-[#161616] border border-white/10 hover:border-[var(--crest-amber)]/50 transition-colors group"
                        style={{ transitionDelay: "80ms" }}
                      >
                        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
                          <Mail size={16} strokeWidth={2} />
                        </div>
                        <div>
                          <div className="font-display uppercase tracking-[0.25em] text-[10px] text-white/50 mb-1.5">
                            Email
                          </div>
                          <div className="font-body font-medium text-sm md:text-base text-white group-hover:text-[var(--crest-amber)] transition-colors break-all">
                            info@crestlineroofing.ca
                          </div>
                          <div className="font-body text-xs text-white/40 mt-1">
                            48-hour response
                          </div>
                        </div>
                      </a>

                      <div
                        data-testid="contact-hours"
                        className="reveal flex items-start gap-4 p-5 bg-[#161616] border border-white/10"
                        style={{ transitionDelay: "160ms" }}
                      >
                        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
                          <Clock size={16} strokeWidth={2} />
                        </div>
                        <div>
                          <div className="font-display uppercase tracking-[0.25em] text-[10px] text-white/50 mb-1.5">
                            Hours
                          </div>
                          <div className="font-body text-sm text-white leading-relaxed">
                            Mon–Fri 7:00 am – 6:00 pm
                            <br />
                            Sat 8:00 am – 4:00 pm
                            <br />
                            <span className="text-[var(--crest-amber)]">
                              24/7 emergency dispatch
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Compact service area teaser removed — full section appears below */}
                  </aside>

                  {/* Form */}
                  <div className="lg:col-span-8">
                    <div className="bg-[#161616] border border-white/10 p-8 md:p-12">
                      {!submitted ? (
                        <>
                          <h3
                            data-testid="contact-form-heading"
                            className="reveal font-display uppercase font-extrabold text-white tracking-tight leading-[0.95] mb-4"
                            style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)" }}
                          >
                            Request your free estimate.
                          </h3>
                          <p className="reveal font-body text-sm text-white/60 mb-8 max-w-md">
                            Every field below helps us prepare a tighter, faster quote — no field is sold or shared.
                          </p>

                          <form onSubmit={onSubmit} className="space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                              <Field label="Name" id="c-name">
                                <Input
                                  id="c-name"
                                  data-testid="contact-input-name"
                                  required
                                  value={form.name}
                                  onChange={update("name")}
                                  placeholder="Jane Holloway"
                                  className="bg-[#0d0d0d] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                                />
                              </Field>
                              <Field label="Phone" id="c-phone">
                                <Input
                                  id="c-phone"
                                  data-testid="contact-input-phone"
                                  required
                                  type="tel"
                                  value={form.phone}
                                  onChange={update("phone")}
                                  placeholder="(778) 555-0123"
                                  className="bg-[#0d0d0d] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                                />
                              </Field>
                            </div>
                            <Field label="Email" id="c-email">
                              <Input
                                id="c-email"
                                data-testid="contact-input-email"
                                required
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="jane@example.com"
                                className="bg-[#0d0d0d] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                              />
                            </Field>
                            <Field label="Property Address" id="c-address">
                              <Input
                                id="c-address"
                                data-testid="contact-input-address"
                                required
                                value={form.address}
                                onChange={update("address")}
                                placeholder="123 Maple Street, Burnaby BC"
                                className="bg-[#0d0d0d] border-white/10 text-white placeholder:text-white/30 rounded-none h-12"
                              />
                            </Field>
                            <Field label="Service Type" id="c-service">
                              <Select
                                value={form.service}
                                onValueChange={(v) =>
                                  setForm((f) => ({ ...f, service: v }))
                                }
                              >
                                <SelectTrigger
                                  id="c-service"
                                  data-testid="contact-input-service"
                                  className="bg-[#0d0d0d] border-white/10 text-white rounded-none h-12 data-[placeholder]:text-white/30"
                                >
                                  <SelectValue placeholder="Choose a service" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white rounded-none">
                                  {SERVICE_TYPES.map((s) => (
                                    <SelectItem
                                      key={s}
                                      value={s}
                                      className="rounded-none focus:bg-[var(--crest-amber)] focus:text-[#121212]"
                                    >
                                      {s}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </Field>
                            <Field label="Additional details" id="c-details">
                              <Textarea
                                id="c-details"
                                data-testid="contact-input-details"
                                rows={5}
                                value={form.details}
                                onChange={update("details")}
                                placeholder="Anything we should know — roof age, leaks, insurance claim, neighborhood considerations, etc."
                                className="bg-[#0d0d0d] border-white/10 text-white placeholder:text-white/30 rounded-none resize-none"
                              />
                            </Field>

                            <button
                              type="submit"
                              data-testid="contact-submit"
                              disabled={sending}
                              className="w-full group inline-flex items-center justify-center gap-3 font-display uppercase tracking-wider font-bold text-base px-9 py-5 bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors duration-300 mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {sending ? (
                                <>
                                  <Loader2 size={18} className="animate-spin" />
                                  Sending…
                                </>
                              ) : (
                                <>
                                  Request My Estimate
                                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </>
                              )}
                            </button>
                            {errorMsg && (
                              <p
                                data-testid="contact-error"
                                className="font-body text-xs text-red-300 text-center bg-red-500/10 border border-red-500/30 px-3 py-2"
                              >
                                {errorMsg}
                              </p>
                            )}
                            <p className="font-body text-[11px] text-white/40 text-center">
                              By submitting, you agree to be contacted by Crestline Roofing Ltd. about your project.
                            </p>
                          </form>
                        </>
                      ) : (
                        <div data-testid="contact-thankyou" className="text-center py-8">
                          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[var(--crest-amber)] text-[#121212]">
                            <CheckCircle2 size={32} strokeWidth={2} />
                          </div>
                          <h3 className="font-display uppercase font-extrabold text-3xl md:text-4xl text-white tracking-tight leading-[0.95] mb-4">
                            Request received.
                          </h3>
                          <p className="font-body text-base text-white/65 max-w-md mx-auto leading-relaxed">
                            Thanks
                            {form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                            A Crestline project lead will reach out within 48 hours to schedule your site visit.
                          </p>
                          <a
                            href="tel:+17789176817"
                            className="inline-flex items-center gap-3 mt-8 font-display uppercase tracking-wider font-bold text-sm px-7 py-4 border border-[var(--crest-amber)] text-[var(--crest-amber)] hover:bg-[var(--crest-amber)] hover:text-[#121212] transition-colors"
                          >
                            Call (778) 917-6817
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Service area section — H2 */}
            <section
              data-testid="contact-service-area"
              className="relative shingle-texture bg-[var(--crest-slate)] py-20 md:py-28"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                  <div className="lg:col-span-5">
                    <div className="reveal flex items-center gap-3 mb-6">
                      <span className="h-px w-12 bg-[var(--crest-amber)]" />
                      <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                        Service Area
                      </span>
                    </div>
                    <h2
                      data-testid="contact-area-heading"
                      className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
                      style={{ fontSize: "clamp(2rem, 4.5vw, 3.6rem)" }}
                    >
                      We Serve Vancouver &amp; the Fraser Valley.
                    </h2>
                    <p className="reveal font-body text-base md:text-lg text-white/65 leading-relaxed mt-6 max-w-md">
                      Local crews dispatched daily across the Lower Mainland — from the North Shore to Mission. If you&apos;re unsure whether we cover your neighbourhood, call us and ask.
                    </p>
                    <a
                      href="tel:+17789176817"
                      data-testid="contact-area-phone"
                      className="reveal inline-flex items-center gap-3 mt-8 font-display uppercase tracking-wider font-bold text-sm px-7 py-4 bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors"
                    >
                      <Phone size={16} />
                      Call (778) 917-6817
                    </a>
                  </div>
                  <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICE_AREAS.map((c, i) => (
                      <div
                        key={c}
                        data-testid={`contact-area-${i}`}
                        className="reveal flex items-center gap-3 p-4 bg-[#141d2e] border border-white/10"
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        <MapPin size={12} className="text-[var(--crest-amber)] flex-shrink-0" />
                        <span className="font-display uppercase tracking-[0.15em] text-xs font-semibold text-white/85">
                          {c}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Google Maps embed */}
            <section
              data-testid="contact-map"
              className="relative bg-[var(--crest-charcoal)] pt-16 md:pt-20 pb-20 md:pb-24"
            >
              <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal flex items-center gap-3 mb-6">
                  <span className="h-px w-12 bg-[var(--crest-amber)]" />
                  <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
                    Find Us
                  </span>
                </div>
                <h3
                  className="reveal font-display uppercase font-bold text-white leading-[0.95] tracking-tight mb-8"
                  style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)" }}
                >
                  11967 80 Ave, Delta, BC V4C 0E2
                </h3>

                <div
                  data-testid="contact-map-frame"
                  className="reveal relative w-full overflow-hidden border border-white/10"
                  style={{ height: "400px" }}
                >
                  <iframe
                    title="Crestline Roofing Ltd. — 11967 80 Ave, Delta, BC V4C 0E2"
                    src="https://www.google.com/maps?q=11967+80+Ave,+Delta,+BC+V4C+0E2&hl=en&z=15&output=embed"
                    width="100%"
                    height="400"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    style={{ border: 0, display: "block", width: "100%", height: "100%" }}
                  />
                </div>

                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=11967+80+Ave,+Delta,+BC+V4C+0E2"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-directions"
                  className="reveal inline-flex items-center gap-2 mt-6 font-display uppercase tracking-[0.3em] text-xs font-bold text-[var(--crest-amber)] border-b border-[var(--crest-amber)]/40 pb-1.5 hover:border-[var(--crest-amber)] transition-colors"
                >
                  <MapPin size={14} />
                  Get directions
                </a>
              </div>
            </section>
          </main>
        )}
      </PageLayout>
    </>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="font-display uppercase tracking-[0.25em] text-[10px] font-semibold text-white/60 mb-2 block"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
